import DOMPurify from 'dompurify';
import type { UponSanitizeAttributeHookEvent } from 'dompurify';

// 以下のALLOWED_*の定数は、帳票インポートのAPI(plan_templates/update.php)でExcel→HTML変換する際に使用している内容
const ALLOWED_TAGS = ['table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col', 'a', 'br', 'nobr'];

const ALLOWED_ATTR = ['class', 'style', 'colspan', 'rowspan', 'cellspacing', 'href', 'target', 'data-field-name'];

const ALLOWED_HREF_SCHEMES = ['https', 'http', 'mailto'];

const ALLOWED_CLASS_NAMES = ['excel', 'input-field'];

const FIELD_NAME_ATTR = 'data-field-name';

// NOTE: 後方に}}が存在する場合のみAngular式として評価されるため、{{を除去する
const ANGULAR_INTERPOLATION_START = /\{\{(?=[\s\S]*\}\})/g;

/**
 * オリジナルタグをバリデーションする以前に、空白があるオリジナルタグがすでに存在し、後方互換を保つために空白を許可する必要がある
 * DOMPurify は属性値を trim するため、前後に空白があるタグ名は保存済みのオリジナルタグのキーと一致しなくなるので値が表示されなくなる
 */
const keepFieldNameWhitespace = (node: Element, event: UponSanitizeAttributeHookEvent): void => {
  if (event.attrName !== FIELD_NAME_ATTR) {
    return;
  }

  const raw = node.getAttribute(FIELD_NAME_ATTR);
  if (raw !== null && raw.trim() === event.attrValue) {
    event.attrValue = raw;
  }
};

const restrictAngularSurface = (node: Element): void => {
  const className = node.getAttribute('class');
  if (className !== null) {
    const allowed = className.split(/\s+/).filter((name) => ALLOWED_CLASS_NAMES.includes(name));
    if (allowed.length > 0) {
      node.setAttribute('class', allowed.join(' '));
    } else {
      node.removeAttribute('class');
    }
  }

  if (node.tagName === 'A') {
    const href = node.getAttribute('href');
    const hrefLower = href !== null ? href.toLowerCase() : null;
    if (hrefLower !== null && !ALLOWED_HREF_SCHEMES.some((scheme) => hrefLower.startsWith(`${scheme}:`))) {
      node.removeAttribute('href');
    }
    if (node.getAttribute('target') === '_blank') {
      node.setAttribute('rel', 'noopener noreferrer');
    }
  }
};

/**
 * compile ディレクティブ（$compile 経由で表示する帳票テンプレ）の無害化を行う
 */
export function sanitize(content: string): string {
  // フックは DOMPurify のシングルトンに積まれるため、他モジュールの常設フックを壊さないよう毎回外す。
  DOMPurify.addHook('uponSanitizeAttribute', keepFieldNameWhitespace);
  DOMPurify.addHook('afterSanitizeAttributes', restrictAngularSurface);
  try {
    return DOMPurify.sanitize(content, { ALLOWED_TAGS, ALLOWED_ATTR, ALLOW_DATA_ATTR: false }).replace(
      ANGULAR_INTERPOLATION_START,
      ''
    );
  } finally {
    DOMPurify.removeHook('afterSanitizeAttributes', restrictAngularSurface);
    DOMPurify.removeHook('uponSanitizeAttribute', keepFieldNameWhitespace);
  }
}
